<template>
    <dynamic-content-frame
        test-id-prefix="modal"
        :header="header"
        :loading="loading"
        :root-classes="rootClasses"
        :content-classes="contentClasses"
        @close="$emit('close')"
    >
        <template #header v-if="$slots.header">
            <slot name="header" />
        </template>
        <slot />
        <template #actions v-if="$slots.actions">
            <slot name="actions" />
        </template>
    </dynamic-content-frame>
</template>

<script setup lang="ts">
    // Generic modal wrapper for the dynamic modals. Used to display a white box
    // with a header/content/actions slot and a close button.
    // Note that you can use dynamicModals without this wrapper, it's just for convenience.
    import { computed, useSlots } from 'vue';
    import DynamicContentFrame from '@/components/dynamicModals/DynamicContentFrame.vue';
    import { resolveContentPadding } from '@/components/dynamicModals/contentPadding.ts';

    interface Emits {
        (e: 'close'): void;
    }
    defineEmits<Emits>();

    const props = withDefaults(
        defineProps<{
            header?: string;
            loading?: boolean;
            useContentPaddingX?: boolean;
            useContentPaddingY?: boolean;
            shouldUseMobileFullscreenLayout?: boolean;
            useFixedLayout?: boolean;
        }>(),
        {
            useContentPaddingX: true,
            useContentPaddingY: true,
            shouldUseMobileFullscreenLayout: false,
            // When true the modal uses a flex layout with fixed height so actions stay pinned.
            useFixedLayout: false,
        }
    );

    const $slots = useSlots();

    const rootClasses = computed(() => {
        const baseClasses = [
            'bg-white',
            'shadow-lg',
            'max-w-[100vw]',
            'xl:max-w-2xl',
            '3xl:max-w-3xl',
            'min-w-[320px]',
            'max-h-[100vh]',
        ];

        if (props.shouldUseMobileFullscreenLayout) {
            return [
                ...baseClasses,
                'flex',
                'flex-col',
                'overflow-hidden',
                'min-h-0',
                'h-screen',
                'md:!h-[680px] md:rounded-lg',
            ];
        }

        if (props.useFixedLayout) {
            return [...baseClasses, 'flex', 'flex-col', 'overflow-hidden', 'min-h-0'];
        }

        return [...baseClasses, 'overflow-auto', 'rounded-lg'];
    });

    const contentClasses = computed(() => {
        const classes: Record<string, boolean> = {
            ...resolveContentPadding(props.useContentPaddingX, props.useContentPaddingY),
            'border-b border-gray-200': !!$slots.actions,
        };

        if (props.shouldUseMobileFullscreenLayout || props.useFixedLayout) {
            classes['flex-1'] = true;
            classes['min-h-0'] = true;
            classes['overflow-auto'] = true;
        }

        return classes;
    });
</script>
